import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Commerce, CommerceStorefrontExperience } from "@teqtank/commerce-sdk";

@customElement("teqtank-storefront-notice")
export class TeqtankStorefrontNotice extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ type: Boolean }) public accessor dismissible = true;
    @state() private accessor storefront: CommerceStorefrontExperience | undefined;
    @state() private accessor dismissed = false;
    private unsubscribe: (() => void) | undefined;

    public static override styles = css`
        :host {
            display: block;
            container-type: inline-size;
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }
        .notice {
            display: flex;
            align-items: center;
            gap: 0.65rem;
            padding: 0.65rem 0.85rem;
            border: 1px solid
                color-mix(in srgb, var(--teqtank-color-warning, #a26313) 30%, transparent);
            border-radius: 0.8rem;
            background: color-mix(
                in srgb,
                var(--teqtank-color-warning, #a26313) 8%,
                var(--teqtank-color-surface, #fff)
            );
            color: var(--teqtank-color-text, #24231f);
            font-size: 0.875rem;
        }
        .icon {
            display: grid;
            place-items: center;
            width: 1.5rem;
            height: 1.5rem;
            flex: 0 0 auto;
            border-radius: 50%;
            background: color-mix(in srgb, var(--teqtank-color-warning, #a26313) 15%, transparent);
            color: var(--teqtank-color-warning, #8a520f);
            font-weight: 800;
        }
        p {
            margin: 0;
            flex: 1;
        }
        button {
            border: 0;
            background: transparent;
            color: inherit;
            font: inherit;
            cursor: pointer;
            padding: 0.25rem;
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();
        this.bind();
    }
    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) this.bind();
    }
    public override disconnectedCallback(): void {
        this.unsubscribe?.();
        super.disconnectedCallback();
    }
    protected override render() {
        if (this.dismissed || this.storefront?.status !== "unavailable") return html``;
        return html`<div class="notice" part="notice" role="status">
            <span class="icon" part="icon" aria-hidden="true">!</span>
            <p part="message">
                ${this.storefront.notice ?? "This representative site is unavailable. You can continue shopping on the main site."}
            </p>
            ${
                this.dismissible
                    ? html`<button
                          part="dismiss"
                          type="button"
                          aria-label="Dismiss notification"
                          @click=${() => {
                              this.dismissed = true;
                          }}
                      >
                          ×
                      </button>`
                    : null
            }
        </div>`;
    }
    private bind(): void {
        this.unsubscribe?.();
        if (!this.commerce) return;
        this.storefront = this.commerce.storefront.current;
        this.unsubscribe = this.commerce.storefront.onChanged((event) => {
            this.dismissed = false;
            this.storefront = event.storefront;
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-storefront-notice": TeqtankStorefrontNotice;
    }
}
